// Dashboard-only formatting that has no app-wide equivalent. Currency reuses the shared
// formatPrice (with its new decimals arg) directly at the call sites; percentages and durations
// reuse the shared formatNumber here. Missing values render as an em dash.
import { useCallback } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { formatNumber } from '@utils/utils.tsx';

const EMPTY = '—';

// Percentage with a fixed number of decimals (ramp utilization & finished → 2). The backend
// sends a 0–1 fraction, so scale to 0–100 for display.
export const formatPercent = (value: number | null | undefined, decimals = 2): string => (value == null ? EMPTY : `${formatNumber(value * 100, decimals, decimals)} %`);

// Duration in minutes → single rounded unit: minutes (< 2 h), hours (< 24 h), else days.
// Returns '—' when the value is missing. Used for handling/scheduled time and time-to-expedition.
export const useDashboardDuration = () => {
  const { tr } = useTranslationLgs();
  return useCallback(
    (minutes?: number | null): string => {
      if (minutes == null) return EMPTY;
      if (minutes < 120) return `${formatNumber(Math.round(minutes), 0, 0)} ${tr('dashboard.unit.minutes', 'min')}`;
      if (minutes < 1440) return `${formatNumber(Math.round(minutes / 60), 0, 0)} ${tr('dashboard.unit.hours', 'h')}`;
      return `${formatNumber(Math.round(minutes / 1440), 0, 0)} ${tr('dashboard.unit.days', 'days')}`;
    },
    [tr],
  );
};